import React, { useEffect, useState } from "react";
import { contactDetails } from "../data/siteContent";

const initialFormData = {
  name: "",
  email: "",
  subject: "",
  message: "",
  contactType: "General",
};

function ContactUs() {
  const [formData, setFormData] = useState(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState("");
  const [successTimeoutId, setSuccessTimeoutId] = useState(null);

  const contactEndpoint =
    process.env.REACT_APP_CONTACT_ENDPOINT ||
    "http://peterosodo-002-site1.ltempurl.com/contact";

  useEffect(() => {
    return () => {
      if (successTimeoutId) {
        window.clearTimeout(successTimeoutId);
      }
    };
  }, [successTimeoutId]);

  const handleChange = (event) => {
    setFormData((currentValue) => ({
      ...currentValue,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    setIsSent(false);

    try {
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:.]/g, "")
        .slice(0, 14);
      const url = `${contactEndpoint}?timestamp=${timestamp}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "text/plain",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          ipAddress: "",
          userAgent: navigator.userAgent,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.isSuccess) {
        throw new Error(
          result.errorMessages?.[0] || result.message || "Failed to send message"
        );
      }

      setIsSent(true);
      setFormData(initialFormData);

      const timeoutId = window.setTimeout(() => setIsSent(false), 5000);
      setSuccessTimeoutId(timeoutId);
    } catch {
      setError(
        "We could not send your message right now. Please try again later or email info.operatingsoftware@gmail.com."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="contact section">
      <div className="container section-title" data-aos="fade-up">
        <h2>Contact</h2>
        <p>
          Tell us what you are building, what is blocking you, or where you need
          a stronger software partner. We will take it from there.
        </p>
      </div>

      <div className="container" data-aos="fade-up" data-aos-delay="100">
        <div className="row gy-4">
          <div className="col-lg-5">
            <div className="info-wrap">
              <div className="info-item d-flex" data-aos="fade-up" data-aos-delay="200">
                <i className="bi bi-geo-alt flex-shrink-0"></i>
                <div>
                  <h3>Address</h3>
                  <p>{contactDetails.address}</p>
                </div>
              </div>

              <div className="info-item d-flex" data-aos="fade-up" data-aos-delay="300">
                <i className="bi bi-telephone flex-shrink-0"></i>
                <div>
                  <h3>Call Us</h3>
                  <p>{contactDetails.phone}</p>
                </div>
              </div>

              <div className="info-item d-flex" data-aos="fade-up" data-aos-delay="400">
                <i className="bi bi-envelope flex-shrink-0"></i>
                <div>
                  <h3>Email Us</h3>
                  <p>{contactDetails.email}</p>
                </div>
              </div>

              <iframe
                title="Operating Software location"
                src={contactDetails.mapEmbedUrl}
                style={{ border: 0, width: "100%", height: "270px" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          <div className="col-lg-7">
            <form onSubmit={handleSubmit} className="php-email-form" data-aos="fade-up" data-aos-delay="200">
              <div className="row gy-4">
                <div className="col-md-6">
                  <label htmlFor="name-field" className="pb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name-field"
                    className="form-control"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>

                <div className="col-md-6">
                  <label htmlFor="email-field" className="pb-2">
                    Your Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    id="email-field"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>

                <div className="col-md-12">
                  <label htmlFor="subject-field" className="pb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="subject"
                    id="subject-field"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>

                <div className="col-md-12">
                  <label htmlFor="contact-type-field" className="pb-2">
                    Contact Type
                  </label>
                  <select
                    className="form-control"
                    name="contactType"
                    id="contact-type-field"
                    value={formData.contactType}
                    onChange={handleChange}
                    disabled={isLoading}
                  >
                    <option value="General">General Inquiry</option>
                    <option value="Project">New Project</option>
                    <option value="Support">Technical Support</option>
                    <option value="Partnership">Partnership</option>
                  </select>
                </div>

                <div className="col-md-12">
                  <label htmlFor="message-field" className="pb-2">
                    Message
                  </label>
                  <textarea
                    className="form-control"
                    name="message"
                    rows="10"
                    id="message-field"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    disabled={isLoading}
                  ></textarea>
                </div>

                <div className="col-md-12 text-center">
                  {isLoading ? <div className="loading">Sending your message...</div> : null}

                  {error ? (
                    <div className="error-message contact-status visible" role="alert">
                      {error}
                    </div>
                  ) : null}

                  {isSent ? (
                    <div className="sent-message contact-status visible" role="status">
                      Your message has been sent successfully. Thank you for
                      reaching out.
                    </div>
                  ) : null}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`btn btn-primary ${isLoading ? "disabled" : ""}`}
                  >
                    {isLoading ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactUs;
